/**
 * Persistence for the most-recently-requested agreement in the Drafting tab.
 * Used to drive the bubble palette: pin the last one to position 1, give it
 * a brand-blue outline (visited-link affordance), and bump the last
 * predefined bubble out when the memory is a custom request.
 */

/* global localStorage */

import { AgreementType } from './agreementTypes';

const STORAGE_KEY = 'signus.lastAgreementType';

/**
 * Snapshot persisted to localStorage. Custom requests carry their full
 * AgreementType (with questions + customDescription) so we can reconstruct
 * the form when the user reclicks the bubble. Predefined types only need
 * the id since the catalog is the source of truth.
 */
export interface AgreementHistoryEntry {
  id: string;
  label: string;
  isCustom: boolean;
  /** Only populated for custom entries — the full type object to reconstruct. */
  customType?: AgreementType;
}

export function getLastAgreement(): AgreementHistoryEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AgreementHistoryEntry;
  } catch {
    return null;
  }
}

export function setLastAgreement(type: AgreementType): void {
  const entry: AgreementHistoryEntry = {
    id: type.id,
    label: type.label,
    isCustom: type.isCustom === true,
    customType: type.isCustom ? type : undefined,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota / privacy mode — fail silently.
  }
}
